import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useOutletContext, useParams } from 'react-router-dom';
import PerfectScrollbar from 'react-perfect-scrollbar';

import InputItem from '../Forms/InputItem/InputItem';
import FormButton from '../Forms/FormButton/FormButton';
import Fader from '../Loaders/Fader';

import { createConstituent, saveConstituent } from '../../../http/constituentAPI';
import { setNotify } from '../../../datastore/actions/actionActions';
import { checkPostalCode } from '../../../util/HelperFunctions';
import useOutsideClick from '../../../hooks/useOutsideClick';

import './ConstituentForm.scss';

const ConstituentForm = ({ isOpen, onCloseHandler, constituentData, handleGetConstituentData }) => {
  const dispatch = useDispatch();

  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [address, setAddress] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [saving, setSaving] = useState(false);

  const config = useSelector((state) => state.config.config);

  const { userInfo } = useOutletContext();

  const { id } = useParams();
  const { pathname } = useLocation();
  const isConstituentPage = pathname.startsWith('/constituent/');

  const windowRef = useRef();

  useEffect(() => {
    if (constituentData) {
      setName(constituentData.name);
      setPhone(constituentData.phone);
      setEmail(constituentData.email);
      setAddress(constituentData.address);
      setPostalCode(constituentData.postal_code);
    }
  }, [constituentData]);

  const checkIsValid = () => {
    if (name && postalCode) {
      return true;
    }

    return false;
  };

  const handleSuccess = (message) => {
    dispatch(
      setNotify({
        status: true,
        type: 'success',
        title: 'Success',
        message,
      }),
    );

    handleGetConstituentData(isConstituentPage ? id : userInfo.id);
    onCloseHandler();
  };

  const handleSave = async () => {
    if (checkIsValid()) {
      if (checkPostalCode(postalCode)) {
        setSaving(true);

        const res = await saveConstituent(
          config,
          constituentData.id,
          userInfo.id,
          name,
          phone,
          email,
          address,
          postalCode.toUpperCase().trim(),
        );

        if (res.status === 200) {
          handleSuccess(res.data.message);
        } else {
          dispatch(
            setNotify({
              status: true,
              type: 'error',
              title: 'Error',
              message: res.data.message,
            }),
          );
        }
        setSaving(false);
      } else {
        dispatch(
          setNotify({
            status: true,
            type: 'error',
            title: 'Error',
            message:
              'Invalid postal code format. Please enter a 6-character code in the format C#C#C# (C - letter, # - digit)',
          }),
        );
      }
    } else {
      dispatch(
        setNotify({
          status: true,
          type: 'error',
          title: 'Error',
          message: 'Fill in all required fields',
        }),
      );
    }
  };

  const handleCreate = async () => {
    if (checkIsValid()) {
      if (checkPostalCode(postalCode)) {
        setSaving(true);

        const res = await createConstituent(
          config,
          userInfo.id,
          name,
          phone,
          email,
          address,
          postalCode.toUpperCase().trim(),
        );

        if (res.status === 201) {
          handleSuccess(res.data.message);
        } else {
          dispatch(
            setNotify({
              status: true,
              type: 'error',
              title: 'Error',
              message: res.data.message,
            }),
          );
        }
        setSaving(false);
      } else {
        dispatch(
          setNotify({
            status: true,
            type: 'error',
            title: 'Error',
            message:
              'Invalid postal code format. Please enter a 6-character code in the format C#C#C# (C - letter, # - digit)',
          }),
        );
      }
    } else {
      dispatch(
        setNotify({
          status: true,
          type: 'error',
          title: 'Error',
          message: 'Fill in all required fields',
        }),
      );
    }
  };

  useOutsideClick(windowRef, null, isOpen, onCloseHandler);

  return ReactDOM.createPortal(
    <div className="login-wrap feedback">
      <div className={`login-view ${saving ? 'saving' : ''}`}>
        <div ref={windowRef} className="login-box feedback__box">
          {!saving ? (
            <div className="feedback__title">{constituentData ? 'Edit Constituent' : 'Create Constituent'}</div>
          ) : (
            <>
              <div className="info__title">{constituentData ? 'Saving Constituent' : 'Creating Constituent'}</div>
              <div className="info__text">The process should take no longer than 30 seconds. Please Wait...</div>
            </>
          )}
          {!saving ? (
            <>
              <div className="feedback__wrapper">
                <PerfectScrollbar
                  options={{
                    wheelPropagation: false,
                    autoHide: false,
                  }}
                >
                  <div className="feedback__form">
                    <InputItem label="Name *" type="text" onChange={(e) => setName(e.target.value)} value={name} />
                    <InputItem label="Phone" type="text" onChange={(e) => setPhone(e.target.value)} value={phone} />
                    <div className="feedback__hint">Multiple values should be separated with a semicolon (;)</div>
                    <InputItem label="Email" type="email" onChange={(e) => setEmail(e.target.value)} value={email} />
                    <div className="feedback__hint">Multiple values should be separated with a semicolon (;)</div>
                    <InputItem
                      label="Address"
                      type="text"
                      onChange={(e) => setAddress(e.target.value)}
                      value={address}
                    />
                    <InputItem
                      label="Postal Code *"
                      type="text"
                      onChange={(e) => setPostalCode(e.target.value)}
                      value={postalCode}
                    />
                    <div className="feedback__hint">Format: C#C#C# (C - letter, # - digit), e.g. A1A1A1</div>
                  </div>
                </PerfectScrollbar>
              </div>
              <div className="submit">
                <FormButton
                  subLink={'Cancel'}
                  subLinkClick={onCloseHandler}
                  text={constituentData ? 'Save' : 'Submit'}
                  onClick={constituentData ? handleSave : handleCreate}
                />
              </div>
            </>
          ) : (
            <Fader size={50} />
          )}
        </div>
      </div>
    </div>,
    document.getElementById('root'),
  );
};

export default ConstituentForm;
